import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import axios from 'axios';
import type { e53DataResponse } from '../types';

export type TableType = 'pending' | 'inbound' | 'outbound';
export type StateType = 'all' | 'pending' | 'completed';

export interface SortConfig {
  column: string;
  direction: 'ASC' | 'DESC';
}

export interface FilterConfig {
  column: string;
  term: string;
}

interface UseE53DataParams {
  page: number;
  size: number;
  sorts?: SortConfig[];
  filters?: FilterConfig[];
  table?: TableType;
  designatedOperator?: string;
  state?: StateType;
  accountingYear?: string;
  enabled?: boolean;
  searchColumn?: string; // Deprecated: use filters instead
  searchTerm?: string; // Deprecated: use filters instead
}

/**
 * Hook to fetch e53 dataset with pagination and filtering
 * 
 * @param params Pagination, sorting, table type, state, designated operator, accounting year, search column and term, and enabled flag for conditional fetching
 * @returns Query result with filtered e53 data
 */
export function useE53Data({ 
  page, 
  size, 
  sorts, 
  filters,
  table, 
  designatedOperator, 
  state, 
  accountingYear,
  searchColumn, // Legacy support
  searchTerm, // Legacy support
  enabled = true 
}: UseE53DataParams) {
  // Combine filters array with legacy searchColumn/searchTerm for backward compatibility
  const allFilters = useMemo(() => {
    const filterList: FilterConfig[] = filters ? [...filters] : [];
    // Add legacy searchColumn/searchTerm if provided and not already in filters
    if (searchColumn && searchTerm && !filterList.some(f => f.column === searchColumn)) {
      filterList.push({ column: searchColumn, term: searchTerm });
    }
    return filterList;
  }, [filters, searchColumn, searchTerm]);

  return useQuery<e53DataResponse>({
    queryKey: ['e53Data', page, size, sorts, allFilters, table, designatedOperator, state, accountingYear],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('size', String(size));
      
      // Add multiple sorting parameters if provided
      if (sorts && sorts.length > 0) {
        sorts.forEach((sort) => {
          // Format for backend sorting: "fieldName,direction"
          params.append('sort', `${sort.column},${sort.direction}`);
        });
      }
      
      // Add multiple filter parameters if provided
      if (allFilters && allFilters.length > 0) {
        allFilters.forEach((filter) => {
          if (filter.column && filter.term) {
            params.append(filter.column, filter.term);
          }
        });
      }
      
      if (table) {
        params.append('table', table);
        
        if (state && state !== 'all') {
          params.append('state', state);
        }
        
        if (designatedOperator && designatedOperator !== 'ALL') {
          params.append('designatedOperator', designatedOperator);
        }
        
        if (accountingYear && accountingYear !== 'all') {
          params.append('accountingYear', accountingYear);
        }
      }

      const response = await axios.get(`/data-set/e53?${params.toString()}`);
      return response.data;
    },
    staleTime: 0, // Set to 0 to ensure fresh data on pagination changes
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes, but will always refetch
    enabled,
    refetchOnMount: true,
    refetchOnWindowFocus: false
  });
} 